import type { JSX } from "react";
import { FC } from "react";
import { H1Large } from "../atoms/Heading";
import { P1 } from "../atoms/Paragraph";
import styled from "styled-components";
import { Button } from "../atoms/Button";
import { Yugi } from "../atoms/Yugi";
import { StandardSection } from "../templates/StandardSection";
import { Fullscreen } from "../templates/CommonStyles";
import { mobile, tablet } from "../templates/Breakpoints";
import { CalloutSpacer } from "../atoms/Callout";
import { CompaniesBar } from "./CompaniesBar/CompaniesBar";
import { Link } from "@components/atoms";
import { NewStatSection, NewStatSectionProps } from "components/molecules/NewStatSection";
import { StaticRoutes } from "@routes";
import { Trustpilot } from "components/atoms/Trustpilot";

export const HomepageHero: FC<
  React.PropsWithChildren<{
    title: string | JSX.Element;
    description: string;
    callToAction: string;
    clouds?: JSX.Element;
    backgroundColor?: string;
    statBar?: NewStatSectionProps;
  }>
> = ({ title, description, callToAction, clouds, backgroundColor, statBar }) => {
  return (
    <>
      <Fullscreen background={backgroundColor} paddingBackground="#F4F0FF">
        {clouds || null}
        <StyledStandardSection pb="3.5rem">
          <Padding>
            <Flex>
              <TextContainer>
                <TitleContainer>
                  <H1Large pt="1.5rem">{title}</H1Large>
                </TitleContainer>
                <DescriptionContainer>
                  <P1>{description}</P1>
                </DescriptionContainer>
                <ButtonWrapper>
                  <Link href={StaticRoutes.getAQuote}>
                    <Button>{callToAction}</Button>
                  </Link>
                </ButtonWrapper>
                <Trustpilot />
              </TextContainer>
              <ImageWrapper>
                <Yugi />
              </ImageWrapper>
            </Flex>
            <CalloutSpacerWrapper>
              <CalloutSpacer />
            </CalloutSpacerWrapper>
          </Padding>
          <CompaniesBarWrapper>
            <CompaniesBar />
          </CompaniesBarWrapper>
        </StyledStandardSection>
      </Fullscreen>
      {statBar && <NewStatSection {...statBar} />}
    </>
  );
};

const Padding = styled.div`
  width: 100%;
  padding: 2.5rem 0;

  ${tablet} {
    padding: 5rem 0;
  }

  ${mobile} {
    padding: 8rem 0;
  }

  @media only screen and (max-width: 30rem) {
    padding: 2rem 0;
  }
`;

const Flex = styled.div`
  display: flex;

  ${mobile} {
    display: block;
  }

  @media only screen and (max-width: 30rem) {
    padding-bottom: 3rem;
  }
`;

const TextContainer = styled.div`
  flex: 1 0 70%;
  display: block;
  position: relative;
  margin-right: auto;
  max-width: 40.3rem;

  @media only screen and (max-width: 60rem) {
    max-width: 30rem;
  }

  @media only screen and (max-width: 45rem) {
    max-width: 24rem;
    padding-top: 3rem;
  }
`;

const TitleContainer = styled.div`
  max-width: 40.3rem;
`;

const DescriptionContainer = styled.div`
  display: block;
  width: 31.3rem;

  @media only screen and (max-width: 65rem) {
    padding-bottom: 1.5rem;
  }

  max-width: 90%;

  ${mobile} {
    max-width: 100%;
  }
`;

const ImageWrapper = styled.div`
  height: 0;
  transform-origin: left;
  transform: scale(1.1) translate(-2.5rem, 0rem);
  p {
    transform: scale(0.91) translateZ(0);
  }

  & > div {
    margin-left: -15rem;
  }

  @media only screen and (max-width: 87rem) {
    padding-right: 3rem;
    transform: scale(1);
    p {
      transform: scale(1);
    }
  }

  @media only screen and (max-width: 70rem) {
    padding-right: 4rem;
    transform: translate(-4rem, 5rem);
  }

  @media only screen and (max-width: 45rem) {
    transform: scale(0.75) translate(-19rem, 43rem);
    p {
      transform: scale(1.25);
    }
    position: absolute;
    right: -10rem;
  }

  @media only screen and (max-width: 40rem) {
    padding-right: 0rem;
    transform: scale(0.5) translate(-20rem, 63rem);
    p {
      transform: scale(1.67);
    }
  }

  @media only screen and (max-width: 30rem) {
    padding-right: 4rem;

    transform: scale(0.6) translate(-4.2rem, -12rem);
    bottom: 20rem;
  }
`;

const ButtonWrapper = styled.div`
  @media only screen and (max-width: 40rem) {
    text-align: center;
  }
`;

const CompaniesBarWrapper = styled.div`
  padding-top: 8.25rem;

  @media only screen and (max-width: 45rem) {
    padding-top: 0rem;
    padding-bottom: 17rem;
  }

  @media only screen and (max-width: 30rem) {
    padding-top: 0rem;
    padding-bottom: 17rem;
  }
`;

export const CalloutSpacerWrapper = styled.div`
  @media only screen and (max-width: 30rem) {
    display: none;
  }
`;

const StyledStandardSection = styled(StandardSection)`
  @media only screen and (max-width: 45rem) {
    padding-bottom: 21rem;
  }

  @media only screen and (max-width: 40rem) {
    padding-bottom: 10rem;
  }

  @media only screen and (max-width: 30rem) {
    padding-bottom: 13rem;
  }
`;
